// 求人原稿生成(媒体別)の入力ページ。
// 入力:会社情報フォーム or 既存原稿。出力媒体:Indeed / gaten職 / Engage(複数選択可)。
// 設計書: docs/design/ad_generator_design.md
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Header } from '../components/Header';
import { AdGenResult } from '../components/AdGenResult';
import { api, ApiError } from '../lib/api';
import {
  AD_GEN_MEDIA_LABELS,
  type AdGenFormInput,
  type AdGenInputMode,
  type AdGenMedia,
  type AdGenRequest,
  type AdGenResponse,
} from '@shared/adGenerator';

type GenState = 'idle' | 'saving' | 'saved' | 'error';

const MEDIA_ORDER: AdGenMedia[] = ['indeed', 'gaten', 'engage'];

const EMPTY_FORM: AdGenFormInput = {
  companyName: '',
  companyBusiness: '',
  companyStrengths: '',
  companyLocation: '',
  jobTitle: '',
  jobContent: '',
  employmentType: '',
  salary: '',
  workingHoursHoliday: '',
  requirements: '',
  targetPersona: '',
  pitchPoints: '',
};

export default function AdGenerator() {
  const [inputMode, setInputMode] = useState<AdGenInputMode>('form');
  const [form, setForm] = useState<AdGenFormInput>(EMPTY_FORM);
  const [draftText, setDraftText] = useState('');
  const [media, setMedia] = useState<AdGenMedia[]>(['indeed', 'gaten', 'engage']);

  const [state, setState] = useState<GenState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [usedModel, setUsedModel] = useState<string | null>(null);

  const setFormField = <K extends keyof AdGenFormInput>(key: K, value: AdGenFormInput[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
  };

  const toggleMedia = (m: AdGenMedia) => {
    setMedia((prev) => (prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]));
  };

  const submit = async () => {
    if (media.length === 0) {
      setError('出力媒体を 1 つ以上選択してください(Indeed / gaten職 / Engage)。');
      return;
    }
    if (inputMode === 'draft' && !draftText.trim()) {
      setError('既存原稿モードでは原稿テキストの入力が必要です。');
      return;
    }
    setState('saving');
    setError(null);
    const trimmedForm: AdGenFormInput = Object.fromEntries(
      Object.entries(form).map(([k, v]) => [k, typeof v === 'string' ? v.trim() : v]),
    ) as AdGenFormInput;
    const payload: AdGenRequest = {
      inputMode,
      media,
      ...(inputMode === 'form' ? { form: trimmedForm } : { draftText: draftText.trim() }),
    };
    try {
      const res = await api.post<AdGenResponse>('/api/ad-generate', payload);
      setResult(res.result);
      setUsedModel(res.model);
      setState('saved');
      setTimeout(() => setState((s) => (s === 'saved' ? 'idle' : s)), 1500);
    } catch (e) {
      setState('error');
      setError(e instanceof ApiError ? e.message : '生成に失敗しました。');
    }
  };

  return (
    <div className="min-h-screen pb-16">
      <Header />
      <main className="mx-auto max-w-6xl px-4 py-6">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">
              求人原稿の生成(媒体別・項目別)
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              会社情報フォーム or 既存原稿から、選択した媒体の入力欄項目ごとに原稿を生成します。
              足りない情報は推測で埋めず <span className="font-semibold text-rank-1">[要確認]</span> として明示します。
              公開予定の情報のみを入力してください(求職者の個人情報・非公開情報は入れないこと)。
            </p>
          </div>
          <Link to="/" className="btn-secondary">
            ダッシュボードに戻る
          </Link>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* 入力 */}
          <section className="space-y-4">
            {/* モード選択 */}
            <div className="card space-y-3">
              <span className="label">入力モード</span>
              <div className="flex gap-2">
                {([
                  { v: 'form', label: '会社情報を入力(新規)' },
                  { v: 'draft', label: '既存原稿を貼り付け' },
                ] as { v: AdGenInputMode; label: string }[]).map((item) => (
                  <button
                    key={item.v}
                    type="button"
                    onClick={() => setInputMode(item.v)}
                    className={`flex-1 rounded-md border-2 px-3 py-1.5 text-sm font-medium transition ${
                      inputMode === item.v
                        ? 'border-brand bg-brand text-white'
                        : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
              <p className="text-xs text-slate-500">
                ・<strong>会社情報モード</strong>:既存原稿がないクライアント向け。分かる範囲だけ入力。
                空欄は <span className="text-rank-1 font-semibold">[要確認]</span> として出力します。
                <br />・<strong>既存原稿モード</strong>:既存原稿を貼ると、媒体ごとの項目構造に再構成します。
                書かれていない項目は [要確認] になります。
              </p>
            </div>

            {/* form モード */}
            {inputMode === 'form' && (
              <div className="card space-y-4">
                <div>
                  <h3 className="mb-2 text-sm font-bold text-slate-700">会社</h3>
                  <div className="grid gap-3">
                    <FieldText
                      id="g-company-name"
                      label="会社名(正式社名)"
                      value={form.companyName ?? ''}
                      onChange={(v) => setFormField('companyName', v)}
                      placeholder="例:株式会社○○電設"
                    />
                    <FieldArea
                      id="g-company-business"
                      label="事業内容"
                      value={form.companyBusiness ?? ''}
                      onChange={(v) => setFormField('companyBusiness', v)}
                      placeholder="例:住宅・マンションの電気工事/エアコン取付け"
                      minH={56}
                    />
                    <FieldArea
                      id="g-company-strengths"
                      label="会社の強み・特徴"
                      value={form.companyStrengths ?? ''}
                      onChange={(v) => setFormField('companyStrengths', v)}
                      placeholder="例:創業30年/資格取得支援/社用車貸与/家族手当あり"
                      minH={72}
                    />
                    <FieldText
                      id="g-company-location"
                      label="所在地(都道府県+市区町村)"
                      value={form.companyLocation ?? ''}
                      onChange={(v) => setFormField('companyLocation', v)}
                      placeholder="例:愛知県名古屋市港区"
                    />
                  </div>
                </div>

                <div>
                  <h3 className="mb-2 text-sm font-bold text-slate-700">求人</h3>
                  <div className="grid gap-3">
                    <FieldText
                      id="g-job-title"
                      label="募集職種(検索用ワード)"
                      value={form.jobTitle ?? ''}
                      onChange={(v) => setFormField('jobTitle', v)}
                      placeholder="例:電気工事士"
                    />
                    <FieldArea
                      id="g-job-content"
                      label="仕事内容(分かる範囲)"
                      value={form.jobContent ?? ''}
                      onChange={(v) => setFormField('jobContent', v)}
                      placeholder="例:住宅の屋内配線/コンセント増設/エアコン取付け。先輩との2名体制"
                      minH={80}
                    />
                    <div className="grid gap-3 sm:grid-cols-2">
                      <FieldText
                        id="g-employment"
                        label="雇用形態"
                        value={form.employmentType ?? ''}
                        onChange={(v) => setFormField('employmentType', v)}
                        placeholder="例:正社員(中途)"
                      />
                      <FieldText
                        id="g-salary"
                        label="給与(分かる範囲)"
                        value={form.salary ?? ''}
                        onChange={(v) => setFormField('salary', v)}
                        placeholder="例:月給25万〜35万円(資格・経験により)"
                      />
                    </div>
                    <FieldArea
                      id="g-hours-holiday"
                      label="勤務時間・休日(分かる範囲)"
                      value={form.workingHoursHoliday ?? ''}
                      onChange={(v) => setFormField('workingHoursHoliday', v)}
                      placeholder="例:8:00〜17:00/日曜+第2第4土曜休/夏季・年末年始"
                      minH={56}
                    />
                    <FieldArea
                      id="g-requirements"
                      label="応募資格・条件(分かる範囲)"
                      value={form.requirements ?? ''}
                      onChange={(v) => setFormField('requirements', v)}
                      placeholder="例:普通免許/未経験OK/第二種電気工事士あれば優遇"
                      minH={56}
                    />
                  </div>
                </div>

                <div>
                  <h3 className="mb-2 text-sm font-bold text-slate-700">ターゲット・訴求</h3>
                  <div className="grid gap-3">
                    <FieldText
                      id="g-target"
                      label="採用ターゲット像"
                      value={form.targetPersona ?? ''}
                      onChange={(v) => setFormField('targetPersona', v)}
                      placeholder="例:20〜30代未経験/手に職をつけたい層"
                    />
                    <FieldArea
                      id="g-pitch"
                      label="会社・現場の特に推したいポイント"
                      value={form.pitchPoints ?? ''}
                      onChange={(v) => setFormField('pitchPoints', v)}
                      placeholder="例:資格取得費は全額会社負担/先輩から技術を学べる/転勤なし"
                      minH={72}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* draft モード */}
            {inputMode === 'draft' && (
              <div className="card space-y-3">
                <label className="label" htmlFor="g-draft">
                  既存原稿(全文) <span className="text-rank-1">*</span>
                </label>
                <textarea
                  id="g-draft"
                  className="input min-h-[280px] resize-y font-mono text-xs leading-relaxed"
                  value={draftText}
                  onChange={(e) => setDraftText(e.target.value)}
                  placeholder="既存の求人原稿を全文貼り付けてください。各媒体の項目構造に再構成します。"
                />
                <p className="text-xs text-slate-500">
                  ※ 原稿に書かれていない項目は <span className="text-rank-1 font-semibold">[要確認]</span> として出力します(推測で創作しません)。
                </p>
              </div>
            )}

            {/* 媒体選択 */}
            <div className="card space-y-3">
              <span className="label">出力媒体(複数選択可・最低 1 つ)</span>
              <div className="flex flex-wrap gap-2">
                {MEDIA_ORDER.map((m) => {
                  const active = media.includes(m);
                  return (
                    <button
                      key={m}
                      type="button"
                      onClick={() => toggleMedia(m)}
                      className={`rounded-md border-2 px-3 py-1.5 text-sm font-medium transition ${
                        active
                          ? 'border-brand bg-brand text-white'
                          : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      {AD_GEN_MEDIA_LABELS[m]}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button onClick={submit} disabled={state === 'saving'} className="btn-primary">
                {state === 'saving' ? '生成中...' : '原稿を生成する'}
              </button>
              <GenIndicator state={state} />
            </div>

            {error && (
              <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-rank-1">{error}</div>
            )}
          </section>

          {/* 結果 */}
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                生成結果(媒体別・項目別)
              </h2>
              {usedModel && <p className="text-xs text-slate-400">モデル: {usedModel}</p>}
            </div>

            {result === null ? (
              <div className="card grid min-h-[400px] place-items-center text-sm text-slate-400">
                {state === 'saving'
                  ? 'Gemini が生成中です...'
                  : '左で会社情報 or 既存原稿を入力し、媒体を選んで「原稿を生成する」を押してください。'}
              </div>
            ) : (
              <AdGenResult markdown={result} onChange={setResult} />
            )}
          </section>
        </div>
      </main>
    </div>
  );
}

function FieldText({
  id,
  label,
  value,
  onChange,
  placeholder,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <div>
      <label className="label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        type="text"
        className="input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </div>
  );
}

function FieldArea({
  id,
  label,
  value,
  onChange,
  placeholder,
  minH = 72,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  minH?: number;
}) {
  return (
    <div>
      <label className="label" htmlFor={id}>
        {label}
      </label>
      <textarea
        id={id}
        className="input resize-y text-sm"
        style={{ minHeight: `${minH}px` }}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </div>
  );
}

function GenIndicator({ state }: { state: GenState }) {
  const map: Record<GenState, { text: string; color: string }> = {
    idle: { text: '', color: 'text-slate-400' },
    saving: { text: '生成中...', color: 'text-slate-500' },
    saved: { text: '✓ 完了しました', color: 'text-rank-5' },
    error: { text: '失敗しました', color: 'text-rank-1' },
  };
  const v = map[state];
  return <span className={`text-xs ${v.color}`}>{v.text}</span>;
}
